import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '@/lib/axios';
import { API_BASE_URL, DIGIO_DIGILOCKER_BASE_URL, DIGIO_ENVIRONMENT } from '@/config';
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, FileText, Loader2 } from "lucide-react";
import { useLoader } from "../contexts/LoaderContext";
import { toast } from "sonner";
import IntellendLogo from "@/components/IntellendLogo";
import { useJourney } from "../contexts/JourneyContext";

declare global {
	interface Window {
		Digio?: any;
	}
}


const ESignJourney = ({ isBuilderMode = false }: { isBuilderMode?: boolean }) => {

    const navigate = useNavigate();
    const { setLoading, setLoaderMessage } = useLoader();
    const { refresh: refreshJourney } = useJourney();
    const [status, setStatus] = useState<'idle' | 'preparing' | 'signing' | 'completed' | 'failed'>('idle');
    const [documentId, setDocumentId] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const digioRef = useRef<any>(null);

    const [sdkLoaded, setSdkLoaded] = useState(false);
    const [documentReady, setDocumentReady] = useState(false);
    const [checkingDocument, setCheckingDocument] = useState(true);

    // Load Digio SDK
    useEffect(() => {
        if (isBuilderMode) return;
        let mounted = true;
        const script = document.createElement('script');
        script.src = `${DIGIO_DIGILOCKER_BASE_URL}/sdk/v11/digio.js`;
        script.async = true;

        const onLoad = () => {
            if (mounted) {
                console.log("Digio SDK loaded");
                setSdkLoaded(true);
            }
        };

        const onError = () => {
             console.error("Failed to load Digio SDK");
        };

        script.addEventListener("load", onLoad);
        script.addEventListener("error", onError);
        document.body.appendChild(script);

        return () => {
            mounted = false;
            script.removeEventListener("load", onLoad);
            script.removeEventListener("error", onError);
            if (document.body.contains(script)) {
                 document.body.removeChild(script);
            }
        };
    }, []);

    // Poll for document readiness (loan agreement PDF generated by Celery task)
    useEffect(() => {
        if (documentReady) return; // Already ready, stop polling

        let cancelled = false;
        const poll = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/api/check_loan_document_ready/`);
                if (!cancelled && res.data?.ready) {
                    setDocumentReady(true);
                    setCheckingDocument(false);
                    console.log("Loan agreement document is ready");
                }
            } catch (err) {
                console.error("Error checking document readiness:", err);
            }
        };

        // Initial check
        poll();
        // Poll every 3 seconds
        const interval = setInterval(poll, 3000);

        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [documentReady]);

    const initiateESign = async () => {
        if (!sdkLoaded || !window.Digio) {
            toast.error("Digio SDK is not loaded yet. Please wait.");
            return;
        }

        setLoading(true);
        setLoaderMessage("esign");
        setStatus('preparing');
        setErrorMessage(null);

        try {
            const response = await axios.post(`${API_BASE_URL}/api/initiate_esign_request/`);
            const { id, signer_identifier } = response.data;

            if (!id || !signer_identifier) {
                throw new Error("Invalid response received from server.");
            }

            setDocumentId(id);

            // Digio SDK v11: callback MUST be passed in constructor options, not assigned after init
            const digio = new window.Digio({
                environment: DIGIO_ENVIRONMENT,
                logo: `${window.location.origin}/logo.svg`,
                theme: {
                    primaryColor: "#AB3498",
                    secondaryColor: "#000000",
                },
                callback: (digioResponse: any) => {
                    console.log("Digio Callback:", digioResponse);
                    if (digioResponse.hasError) {
                        console.error("Digio signing error:", digioResponse.message);
                        setErrorMessage(digioResponse.message || "Signing failed. Please try again.");
                        setStatus('failed');
                        setLoaderMessage(null);
                        setLoading(false);
                    } else {
                        // Signing completed successfully
                        handleSuccess();
                    }
                },
            });

            try {
                digio.init();
            } catch (initError: any) {
                console.error("Digio init error:", initError);
                setErrorMessage("Failed to initialize verification window.");
                setStatus('failed');
                setLoaderMessage(null);
                setLoading(false);
                return;
            }

            setStatus('signing');

            try {
                digio.submit(id, signer_identifier);
            } catch (popupError: any) {
                console.error("Popup error:", popupError);
                setErrorMessage("Popup blocked! Please allow popups for this site and try again.");
                setStatus('failed');
            }

        } catch (error: any) {
            console.error("eSign Initiation Error:", error);
            setStatus('failed');
            setErrorMessage(error.response?.data?.error || "Failed to initiate eSign request.");
            toast.error("Could not start signing process.");
        } finally {
            setLoaderMessage(null);
            setLoading(false);
        }
    };

    const handleSuccess = async () => {
        setStatus('completed');
        toast.success("Document signed successfully!");
        setLoading(true);
        setLoaderMessage("esign-complete");
        try {
            // Advance journey — use next_page_url from backend so the journey
            // template controls what comes after eSign (doc-push, loan-success, etc.)
            const res = await axios.post(`${API_BASE_URL}/api/complete_esign_step/`);
            await refreshJourney();
            const nextPage = res.data?.next_page_url;
            navigate(nextPage || '/loan-success');
        } catch (error) {
            console.error("Failed to advance journey:", error);
        } finally {
            setLoaderMessage(null);
            setLoading(false);
        }
    };

    const pollStatus = async (id: string) => {
         // Polling can act as a fallback if the callback fails to fire or user closes the popup early
        if (!id || id === 'undefined') {
            console.error("Poll status called without valid ID");
            return;
        }
        try {
            const response = await axios.get(`${API_BASE_URL}/api/esign/status/${id}/`);
            const data = response.data;
            if (data.status === 'signed' || data.status === 'completed' || data.agreement_status === 'completed') {
                handleSuccess();
            } else {
                 if(status === 'signing') {
                      setTimeout(() => pollStatus(id), 10000); // Slower polling just as a backup
                 }
            }
        } catch (error) {
            console.error("Status check failed:", error);
             if(status === 'signing') {
                 setTimeout(() => pollStatus(id), 15000);
             }
        }
    };

    // Trigger fallback polling if stuck in signing mode
    useEffect(() => {
        if (status === 'signing' && documentId) {
             const timer = setTimeout(() => pollStatus(documentId), 10000);
             return () => clearTimeout(timer);
        }
    }, [status, documentId]);


    if (isBuilderMode) {
        return (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
                <div className="text-2xl mb-1">✍️</div>
                <p className="font-semibold text-gray-500">Digio eSign</p>
                <p className="text-xs mt-1">Loads Digio SDK, initiates eSign, opens popup, and advances journey on completion.</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border p-8 text-center">
                <div className="mb-6 flex justify-center">
                    <IntellendLogo size="md" />
                </div>

                {(status === 'idle' || status === 'preparing') && (
                    <>
                        <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FileText className="w-8 h-8" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Sign Loan Agreement</h1>
                        {!documentReady ? (
                            <>
                                <p className="text-gray-500 mb-4">
                                    Preparing your loan agreement document...
                                </p>
                                <Loader2 className="w-8 h-8 text-intellend-green animate-spin mx-auto mb-4" />
                                <p className="text-sm text-gray-400">This may take up to a minute. Please wait.</p>
                            </>
                        ) : (
                            <>
                                <p className="text-gray-500 mb-8">
                                    Please complete the eSign process to finalize your loan application.
                                </p>
                                <Button
                                    onClick={initiateESign}
                                    className="w-full bg-intellend-green hover:bg-intellend-green/90 h-12 text-lg font-bold"
                                    disabled={status === 'preparing' || !sdkLoaded}
                                >
                                    {!sdkLoaded ? (
                                        <>Loading SDK... <Loader2 className="ml-2 w-4 h-4 animate-spin" /></>
                                    ) : status === 'preparing' ? (
                                        <>Starting... <Loader2 className="ml-2 w-4 h-4 animate-spin" /></>
                                    ) : (
                                        'Start eSign'
                                    )}
                                </Button>
                            </>
                        )}
                    </>
                )}

                {status === 'signing' && (
                    <div className="py-12">
                        <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle2 className="w-8 h-8" />
                        </div>
                        <h2 className="text-xl font-semibold mb-2">Signing in Progress</h2>
                        <p className="text-gray-500 mb-6">
                            Please complete the digital signature in the popup window.
                        </p>
                        <Loader2 className="w-8 h-8 text-intellend-green animate-spin mx-auto mb-4" />
                        <p className="text-sm text-gray-400">Waiting for completion...</p>
                    </div>
                )}

                {status === 'completed' && (
                    <div className="py-8">
                        <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle2 className="w-8 h-8" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Successfully Signed!</h2>
                        <p className="text-gray-500 mb-6 font-medium">Your loan agreement has been digitally signed and saved.</p>
                        <Loader2 className="w-6 h-6 text-intellend-green animate-spin mx-auto" />
                        <p className="text-sm text-gray-400 mt-2">Finalizing your application...</p>
                    </div>
                )}

                {status === 'failed' && (
                    <div className="py-8">
                        <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AlertCircle className="w-8 h-8" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Signing Failed</h2>
                        <p className="text-gray-500 mb-6">{errorMessage || "An error occurred during the eSign process."}</p>
                        <Button
                            onClick={() => setStatus('idle')}
                            variant="outline"
                            className="w-full"
                        >
                            Try Again
                        </Button>
                    </div>
                )}
            </div>

            <div id="digio-esign-container"></div>
        </div>
    );
};

export default ESignJourney;
